import { v4 as uuidv4 } from 'uuid'

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import {
  formatAspectRatioFormat,
  formatCornerRadiusFormat,
  formatFromVariableSelect,
  formatFromVariableText,
  formatHorizontalAlignmentFormat,
  formatResponsiveValue,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseAspectRatioFormat,
  parseCornerRadiusFormat,
  parseResponsiveFormat,
  parseSelectFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import { BaseAiComponentInput } from '../../registry'
import type {
  AiAspectRatio,
  ColorVariant,
  HorizontalAlignment,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import imageTemplate from './image.template.json'

export interface IImageComponentSettings {
  // Data settings
  'image-data-src': VariableSelectValue
  'image-data-title': VariableTextValue
  'image-data-like': any
  'image-data-badge': VariableTextValue

  // Design settings
  'image-design-ratio': ResponsiveValue<AiAspectRatio>
  'image-design-scheme': any
  'image-design-component-title-color': any
  'image-design-border-color': ColorVariant
  'image-design-badge-color': any
  'image-design-background-caption': string
  'image-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'image-design-margin': ResponsiveValue<SpacingInputValue>
  'image-design-h-align': ResponsiveValue<HorizontalAlignment>
  'image-design-comp-title-align': ResponsiveValue<HorizontalAlignment>
  'image-design-like-icon': string
  'image-design-text-line-caption': number[]
  'image-design-border': ResponsiveValue<number[]>
  'image-design-shape': ResponsiveValue<string>
  'image-design-typography': {
    'image-design-caption-typography': ResponsiveValue<TypographyValue>
    'image-design-tag-typography': ResponsiveValue<TypographyValue>
  }
  'image-design-hover-effect'?: string // Hover effect: 'none' | 'underline' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'

  // Actions
  'image-action-click': BuilderActions
  'image-action-like-click': BuilderActions
}

export interface IImageComponent extends IBuilderComponent {
  componentId: 'image'
  groupId: 'media'
  settings: IImageComponentSettings
}

export interface AiImageInput extends BaseAiComponentInput {
  src: string // Image URL
  imageTitle?: string[] // Format: ["text:Image Title"] or ["text:Photo of ", "cr:subject"]
  badge?: string[] // Format: ["text:New"] or ["text:Featured"]
  width?: string // Format: "w:100%" or "w:300px" or "w:100% de:w:300px"
  margin?: string // Format: "m:20px" or "mt:20px mb:20px"
  alignment?: string // Format: "h:center" or "h:start ta:h:center"
  titleAlignment?: string // Format: "c:center" or "h:start ta:h:center"
  cornerRadius?: string // Format: "medium" or "small ta:medium de:large"
  typography?: string // Format: "body-medium" or "body-medium ta:body-large"
  aspectRatio?: string // Format: "auto" or "1:1" | "4:3" | "16:9" | "3:2" | "5:4" | "7:5" | "8:10" | "9:16" | "2:1" | "21:9"
  border?: string // Format: "b:3px" or "b:3px de:b:5px" (border width in px)
  hoverEffect?: string // Format: "bounce" or "underline" | 'none' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
  clickAction?: BuilderActions[]
}

const IMAGE_DEFAULT_TEMPLATE = imageTemplate as unknown as IImageComponentSettings

export class ImageComp {
  getTemplate(): IImageComponentSettings {
    return IMAGE_DEFAULT_TEMPLATE
  }

  /**
   * format image component settings to AI image input
   */
  format(settings: IImageComponentSettings): AiImageInput {
    const result: AiImageInput = {
      type: 'image',
      src: formatFromVariableSelect(settings['image-data-src']) || '',
    }

    // Image Title
    if (settings['image-data-title']) {
      const title = formatFromVariableText(settings['image-data-title'])
      if (title.length > 0 && title[0] !== 'text:') {
        result.imageTitle = title
      }
    }

    // Badge
    if (settings['image-data-badge']) {
      const badge = formatFromVariableText(settings['image-data-badge'])
      if (badge.length > 0 && badge[0] !== 'text:') {
        result.badge = badge
      }
    }

    // Width - extract from OverflowSettings
    if (settings['image-design-width']) {
      const width = formatWidthFormat(settings['image-design-width'] as any)
      if (width) {
        result.width = width
      }
    }

    // Margin
    const margin = formatSpacingFormat(settings['image-design-margin'])
    if (margin) {
      result.margin = margin
    }

    // Alignment - map from component title alignment
    const alignment = formatHorizontalAlignmentFormat(settings['image-design-h-align'])
    if (alignment) {
      result.alignment = alignment
    }
    const titleAlignment = formatHorizontalAlignmentFormat(settings['image-design-comp-title-align'])
    if (titleAlignment) {
      result.titleAlignment = titleAlignment
    }

    // Corner Radius
    const cornerRadius = formatCornerRadiusFormat(
      settings['image-design-shape']?.phone || (settings['image-design-shape'] as any)
    )
    if (cornerRadius) {
      result.cornerRadius = cornerRadius
    }

    // Typography - use caption typography
    if (settings['image-design-typography']?.['image-design-caption-typography']) {
      const typography = formatTypographyFormat(settings['image-design-typography']['image-design-caption-typography'])
      if (typography) {
        result.typography = typography
      }
    }

    // Aspect Ratio
    const aspectRatio = formatAspectRatioFormat(settings['image-design-ratio'] as any)
    if (aspectRatio) {
      result.aspectRatio = aspectRatio
    }

    // Border - format as responsive value (e.g., "b:3px" or "b:3px de:b:5px")
    if (settings['image-design-border']) {
      const border = formatResponsiveValue(settings['image-design-border'], (val) => {
        if (Array.isArray(val) && val.length > 0) {
          const borderWidth = val[0]
          return `b:${borderWidth}px`
        }
        return 'b:0px'
      })
      if (border) {
        result.border = border
      }
    }

    // Hover Effect
    if (settings['image-design-hover-effect']) {
      const hoverEffect = settings['image-design-hover-effect']
      if (hoverEffect && hoverEffect !== 'none') {
        result.hoverEffect = hoverEffect
      }
    }

    // Actions (copied as-is)
    if (settings['image-action-click']) {
      result.clickAction = settings['image-action-click'] as any
    }

    return result
  }

  /**
   * parse AI image input to image component settings
   */
  parse(input: AiImageInput): IImageComponentSettings {
    const result = deepMerge(IMAGE_DEFAULT_TEMPLATE, {})

    // Data - Image source (required)
    if (input.src) {
      const parsedSrc = parseSelectFormat(input.src)
      result['image-data-src'] = parsedSrc
      // Preserve id if it was in the original (for internal images)
      if (!parsedSrc.id && (parsedSrc as any).col) {
        parsedSrc.id = uuidv4()
      }
    }

    if (input.imageTitle) {
      result['image-data-title'] = parseToVariableText(input.imageTitle)
    }

    if (input.badge) {
      result['image-data-badge'] = parseToVariableText(input.badge)
    }

    // Design - Width
    if (input.width) {
      result['image-design-width'] = {
        ...parseWidthFormat(input.width),
        overflowSetting: result['image-design-width'].overflowSetting,
      }
    }

    // Design - Margin
    if (input.margin) {
      result['image-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['image-design-h-align'] = alignmentParsed.hAlign
      }
    }

    // Design - Title Alignment
    if (input.titleAlignment) {
      const alignmentParsed = parseAlignmentFormat(input.titleAlignment)
      if (alignmentParsed.hAlign) {
        result['image-design-comp-title-align'] = alignmentParsed.hAlign
      }
    }

    // Design - Shape (corner radius)
    if (input.cornerRadius) {
      result['image-design-shape'] = parseCornerRadiusFormat(input.cornerRadius)
    }

    // Design - Aspect Ratio
    if (input.aspectRatio) {
      result['image-design-ratio'] = parseAspectRatioFormat(input.aspectRatio)
    }

    // Design - Typography (nested structure)
    if (input.typography) {
      const parsedTypography = parseTypographyFormat(input.typography)
      result['image-design-typography']['image-design-caption-typography'] = parsedTypography
      result['image-design-typography']['image-design-tag-typography'] = parsedTypography
    }

    // Design - Border
    if (input.border) {
      result['image-design-border'] = parseResponsiveFormat(input.border, (value) => {
        const match = value.match(/^b:(\d+(?:\.\d+)?)px$/)
        if (!match) return [0]
        const borderWidth = parseInt(match[1], 10)
        return [borderWidth]
      })
    }

    // Hover Effect
    if (input.hoverEffect) {
      result['image-design-hover-effect'] = input.hoverEffect
    }

    // Actions
    if (input.clickAction) {
      // @ts-ignore
      result['image-action-click'] = input.clickAction
    }

    return result
  }
}
